import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Truck,
  Ship,
  Boxes,
  Wallet,
  Table2,
  LogOut,
  FileDown,
  Printer,
  RotateCcw,
  CalendarDays,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FilterContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Executive Overview", icon: LayoutDashboard, end: true },
  { to: "/supplier", label: "Supplier Management", icon: Truck },
  { to: "/operational", label: "Operational", icon: Ship },
  { to: "/volume", label: "Volume", icon: Boxes },
  { to: "/finance", label: "Finance", icon: Wallet },
  { to: "/shipments", label: "Shipment Register", icon: Table2 },
];

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { fromDate, setFromDate, toDate, setToDate, clear, params, options } = useFilters();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await api.get(`/export/excel${qs ? "?" + qs : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "BMG_BI_Export.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel export downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = () => {
    toast.info("Preparing print / PDF view…");
    setTimeout(() => window.print(), 300);
  };

  const dayMin = options?.days?.[0];
  const dayMax = options?.days?.[options.days.length - 1];

  return (
    <div className="flex h-screen overflow-hidden app-canvas">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-col border-r border-white/5 bg-[hsl(var(--sidebar))] text-white lg:flex print:hidden">
        <div className="flex h-16 items-center gap-3 border-b border-white/10 px-5">
          <div className="rounded-md bg-white px-2.5 py-1.5">
            <img src="/xpo-logo.png" alt="XPO" className="h-4 w-auto" />
          </div>
          <div className="h-6 w-px bg-white/15" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">BI Portal</span>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-white/10 font-medium text-white"
                    : "text-white/55 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`absolute left-0 h-5 w-1 rounded-r-full bg-brand transition-opacity ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <item.icon className="h-[18px] w-[18px]" strokeWidth={1.9} />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 flex items-center gap-3 px-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/20 font-mono text-sm font-semibold text-brand">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-white">{user?.name || user?.email}</div>
              <div className="truncate text-xs text-white/40">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Sticky filter/top bar */}
        <header className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-border glass px-4 py-3 sm:px-6 print:hidden">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Period</span>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">From</span>
            <input
              type="date"
              value={fromDate}
              min={dayMin}
              max={toDate || dayMax}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="filter-from-date"
              className="h-9 rounded-lg border border-input bg-background px-3 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/40"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">To</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || dayMin}
              max={dayMax}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="filter-to-date"
              className="h-9 rounded-lg border border-input bg-background px-3 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/40"
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={clear}
            data-testid="filter-reset"
            className="h-9 gap-1 text-muted-foreground"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportExcel}
              disabled={exporting}
              data-testid="export-excel-btn"
              className="h-9 gap-1.5 rounded-lg"
            >
              <FileDown className="h-4 w-4" /> Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportPdf}
              data-testid="export-pdf-btn"
              className="h-9 gap-1.5 rounded-lg"
            >
              <Printer className="h-4 w-4" /> PDF
            </Button>
            <div className="ml-1 hidden items-center gap-2 border-l border-border pl-3 sm:flex">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Client
              </span>
              <img src="/bmg-logo.png" alt="Brand Machine Group" className="h-6 w-auto logo-invert" />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="fade-in mx-auto max-w-[1500px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
